import React, { useState, useEffect, useRef } from 'react';
import { useFirebase } from '../contexts/FirebaseContext';
import { useWordContext } from '../context/WordContext';
import apiClient from '../utils/apiClient';
import '../styles/chatbot.css';
import processChatbotSentence from '../utils/processChatbotSentence';
import ChatbotDictionaryPanel from '../components/ChatbotDictionaryPanel';

const ChatbotTest = () => {
  const { user, isAuthenticated } = useFirebase();
  const { savedWords } = useWordContext();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatMode, setChatMode] = useState('learning'); // 'learning' or 'casual'
  const [username, setUsername] = useState('');
  const [settings, setSettings] = useState({
    learningLanguage: '',  // Start with empty string to avoid default
    languageLevel: 'Beginner',
    spanishRegion: 'Latin America'
  });
  const [languageSpecificWords, setLanguageSpecificWords] = useState([]);
  const [isNewConversation, setIsNewConversation] = useState(true); // Track if this is a new conversation
  const [settingsLoaded, setSettingsLoaded] = useState(false); // Track if settings are loaded
  const [translatedMessages, setTranslatedMessages] = useState({}); // { messageIndex: boolean }
  const [selectedWord, setSelectedWord] = useState(null); // Store the selected word for dictionary
  const [selectedWordSentence, setSelectedWordSentence] = useState(''); // Store the sentence context
  const [dictionaryOpen, setDictionaryOpen] = useState(false); // Dictionary closed by default
  const [chatModeOpen, setChatModeOpen] = useState(true); // Chat mode open by default
  // Custom mode state
  const [useCustomMode, setUseCustomMode] = useState(false); // Whether to use custom mode
  const [customPrompt, setCustomPrompt] = useState(''); // Custom prompt text
  const [customModeOpen, setCustomModeOpen] = useState(false); // Custom mode closed by default
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

    // System prompts template for different modes
    const getSystemPrompt = (mode, language) => {
        // If custom mode is enabled and we have a custom prompt, use it
        if (useCustomMode && customPrompt.trim()) {
          return `${customPrompt.trim()}

You only speak ${language} and therefore you must not speak English even if you're asked to do so. Just apologize and say that you don't speak it. Despite not speaking it, you fully understand it, and can reply in ${language} to messages in other languages.`;
        }

        // Otherwise use regular prompts
        const prompts = {
          learning: `You are a ${language} language professor named Bojee. When chatting with the user, if you ever see their sentence or phrase have any mistakes, you make sure to provide a corrected version before proceeding with your reply. If it's only a portion of the sentence that has a mistake (ex: badly conjugated verb, or incorrect word usage), then you simply provide that corrected section rather than the entire sentence. Make sure to never use English in your replies, even if the user says something in English! You can translate English words into ${language}, but you can never use English yourself. Just apologize and say you don't speak English.'
      
      You use the user's saved ${language} words naturally in conversation to reinforce their memorization. Don't force yourself to use them, but use them if they can naturally fit into the conversation. Try to use a minimum of 3 of the saved words in the whole conversation when possible. DO NOT USE MORE THAN 1 in a single reply. IMPORTANT: DO NOT use asterisks, bold formatting, or any special symbols around these words - just incorporate them naturally as if you're having a real conversation. Specifically NEVER use asterisks (*) in ANY of your responses, as they'll be removed from the display. Important Note: If the saved words are verbs, recognjugate them by varying the tenses and persons.
      
      Other than that, you can discuss creative topics, interesting ideas, and don't limit yourself to any one topic. You can talk about movies, music, books, history, geography, science, technology, relationships, self-development, fun facts, and much more.`,

          casual: `You are a friendly ${language} chatbot named Bojee. Your purpose is to help the user practice ${language} in a casual, conversational way. Make sure to never use English in your replies, even if the user says something in English! You can translate English words into ${language}, but you can never use English yourself. Just apologize and say you don't speak English.'
      
      You use the user's saved ${language} words naturally in conversation to reinforce their memorization. Don't force yourself to use them, but use them if they can naturally fit into the conversation. Try to use a minimum of 3 of the saved words in the whole conversation when possible. DO NOT USE MORE THAN 1 in a single reply. IMPORTANT: DO NOT use asterisks, bold formatting, or any special symbols around these words - just incorporate them naturally as if you're having a real conversation. Specifically NEVER use asterisks (*) in ANY of your responses, as they'll be removed from the display. Important Note: If the saved words are verbs, recognjugate them by varying the tenses and persons.
      
      You can discuss creative topics, interesting ideas, and don't limit yourself to any one topic. You can talk about movies, music, books, history, geography, science, technology, relationships, self-development, fun facts, and much more. Keep the conversation engaging and natural.`
        };

        return prompts[mode];
    };

  // Get default introduction message based on language
  const getDefaultMessage = (language) => {
    // English version of the welcome message for all languages
    const englishWelcomeMessage = `Hi, I'm Bojee! I keep track of all the ${language} words you save and then use them naturally in conversation with you to help you remember them! 

There are two modes to choose from when chatting with me. If you choose Learning Mode, I will correct any mistakes you might make before replying to you. If you choose "Casual Mode", then we'll just chat casually to make your learning more organic and natural. 

Let me know what's on your mind :) Let's take your ${language} to the next level!`;

    // Object containing translations for the intro message for different languages
    const translations = {
      Spanish: {
        role: 'assistant',
        content: `¡Hola, soy Bojee! ¡Yo registro todas las palabras en español que guardas y luego las uso naturalmente en la conversación contigo para ayudarte a recordarlas!

Hay dos modos para elegir cuando chateas conmigo. Si eliges el Modo de Aprendizaje, corregiré cualquier error que puedas cometer antes de responderte. Si eliges el "Modo Casual", simplemente chatearemos de manera informal para hacer tu aprendizaje más orgánico y natural.

Dime qué tienes en mente :) ¡Llevemos tu español al siguiente nivel!`,
        englishTranslation: englishWelcomeMessage
      },
      French: {
        role: 'assistant',
        content: `Salut, je suis Bojee ! Je garde une trace de tous les mots français que tu enregistres et je les utilise naturellement dans la conversation avec toi pour t'aider à les mémoriser !

Il y a deux modes à choisir quand tu discutes avec moi. Si tu choisis le Mode Apprentissage, je corrigerai toutes les erreurs que tu pourrais faire avant de te répondre. Si tu choisis le "Mode Décontracté", nous discuterons simplement de façon informelle pour rendre ton apprentissage plus organique et naturel.

Dis-moi ce qui te préoccupe :) Amenons ton français au niveau supérieur !`,
        englishTranslation: englishWelcomeMessage
      },
      Italian: {
        role: 'assistant',
        content: `Ciao, sono Bojee! Tengo traccia di tutte le parole italiane che salvi e poi le uso naturalmente nella conversazione con te per aiutarti a ricordarle!

Ci sono due modalità tra cui scegliere quando chatti con me. Se scegli la Modalità Apprendimento, correggerò qualsiasi errore che potresti fare prima di risponderti. Se scegli la "Modalità Casual", chatteremo semplicemente in modo informale per rendere il tuo apprendimento più organico e naturale.

Fammi sapere cosa hai in mente :) Portiamo il tuo italiano al livello successivo!`,
        englishTranslation: englishWelcomeMessage
      },
      Japanese: {
        role: 'assistant',
        content: `こんにちは、ボジーです！あなたが保存した日本語の単語をすべて記録し、会話の中で自然に使用して、あなたが覚えるのを助けます！

私とチャットするときに選択できるモードは2つあります。学習モードを選択すると、あなたが間違えた場合は返信する前に修正します。「カジュアルモード」を選択すると、学習をより有機的で自然なものにするために、カジュアルに会話します。

何か考えていることがあれば教えてください :) あなたの日本語を次のレベルに引き上げましょう！`,
        englishTranslation: englishWelcomeMessage
      }
    };

    // Default message with English translation
    const defaultMessage = {
      role: 'assistant',
      content: `Hi, I'm Bojee! I keep track of all the ${language} words you save and then use them naturally in conversation with you to help you remember them! 

There are two modes to choose from when chatting with me. If you choose Learning Mode, I will correct any mistakes you might make before replying to you. If you choose "Casual Mode", then we'll just chat casually to make your learning more organic and natural. 

Let me know what's on your mind :) Let's take your ${language} to the next level!`,
      englishTranslation: englishWelcomeMessage
    };

    // Return the translation for the specified language or default to a generic message
    return translations[language] || defaultMessage;
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Fetch settings and user info on mount
  useEffect(() => {
    const fetchUserData = async () => {
      if (!isAuthenticated || !user) return;
      
      try {
        console.log('Chatbot - Fetching user data');
        
        // Fetch settings to get the learning language
        const settingsResponse = await apiClient.get('/api/settings');
        if (settingsResponse.ok) {
          const settingsData = await settingsResponse.json();
          console.log('Chatbot - Settings fetched:', settingsData);
          
          // Ensure we have a valid language or default to a safe value
          const language = settingsData.learningLanguage || 'Spanish';
          console.log('Chatbot - Language set to:', language);
          
          setSettings({
            ...settingsData,
            learningLanguage: language
          });
          setSettingsLoaded(true); // Mark settings as loaded
          
          // Once we have the language, fetch username
          const profileResponse = await apiClient.get('/api/profile');
          if (profileResponse.ok) {
            const profileData = await profileResponse.json();
            console.log('Chatbot - Profile fetched:', profileData);
            setUsername(profileData.username || user.displayName || 'User');
          }
          
          // Fetch language-specific saved words
          await fetchLanguageSpecificWords(language);
        } else {
          console.error('Chatbot - Failed to fetch settings:', await settingsResponse.text());
          // Set a default if settings fetch fails
          setSettings(prev => ({
            ...prev,
            learningLanguage: 'Spanish'
          }));
          setSettingsLoaded(true);
        }
      } catch (error) {
        console.error('Chatbot - Error fetching user data:', error);
        // Set a default if there's an error
        setSettings(prev => ({
          ...prev,
          learningLanguage: 'Spanish'
        }));
        setSettingsLoaded(true);
      }
    };

    fetchUserData();
  }, [isAuthenticated, user]);

  // When settings are loaded AND this is a new conversation, set the default message
  useEffect(() => {
    if (settingsLoaded && isNewConversation && settings.learningLanguage) {
      console.log(`Chatbot - Setting default message for ${settings.learningLanguage}`);
      setMessages([getDefaultMessage(settings.learningLanguage)]);
      setIsNewConversation(false); // Mark that we've shown the intro message
    }
  }, [settingsLoaded, settings.learningLanguage, isNewConversation]);

  // Fetch saved words specific to the current language
  const fetchLanguageSpecificWords = async (language) => {
    try {
      console.log(`Chatbot - Fetching saved words for ${language}`);
      
      if (!user) {
        console.log('Chatbot - No user authenticated, skipping word fetch');
        return;
      }
      
      const token = await user.getIdToken();
      const response = await fetch(`/api/getUserWords?userId=${user.uid}&language=${language}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // The API returns { words: [...] } format
        let wordArray = [];
        
        if (data && data.words && Array.isArray(data.words)) {
          wordArray = data.words;
          console.log(`Chatbot - Fetched ${wordArray.length} ${language} words:`, wordArray);
        } else if (Array.isArray(data)) {
          // For flexibility, handle both array and { words: [] } formats
          wordArray = data;
          console.log(`Chatbot - Fetched ${wordArray.length} ${language} words:`, wordArray);
        } else {
          console.error(`Chatbot - Invalid response format for ${language} words:`, data);
        }
        
        setLanguageSpecificWords(wordArray);
      } else {
        console.error(`Chatbot - Failed to fetch ${language} words:`, await response.text());
        setLanguageSpecificWords([]);
      }
    } catch (error) {
      console.error(`Chatbot - Error fetching ${language} words:`, error);
      setLanguageSpecificWords([]);
    }
  };

  // Start a new conversation
  const handleNewConversation = () => {
    console.log('Chatbot - Starting new conversation');
    // Clear messages but don't show intro message again
    setMessages([]);
    setInput('');
    setIsNewConversation(true);
    setTranslatedMessages({});
    
    // Reset the selected word
    setSelectedWord(null);
    setSelectedWordSentence('');
  };

  const handleSendMessage = async () => {
    if (!input.trim()) return;
    
    console.log('Chatbot - Sending message');
    setIsLoading(true);
    
    // Add user message to chat
    const newMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, newMessage]);
    setInput('');
    
    try {
      // Check if we need to add the introduction message first
      if (isNewConversation) {
        // No need to keep restarting
        setIsNewConversation(false);
      }
      
      // Only scroll after adding new messages
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
      
      console.log('Chatbot - Preparing request payload');
      
      // Get the language from settings
      const language = settings.learningLanguage || 'Spanish';
      console.log(`Chatbot - Using language: ${language}`);
      
      // Get the appropriate system prompt
      const systemPrompt = getSystemPrompt(chatMode, language);
      console.log(`Chatbot - Using ${useCustomMode ? 'custom' : chatMode} mode prompt`);
      
      // Format saved words for the API
      const savedWordsData = languageSpecificWords.length > 0 ? 
        languageSpecificWords.map(word => word.word || word).join(',') : 
        '';
      console.log(`Chatbot - Using ${languageSpecificWords.length} saved words`);
      
      // Construct conversation history from messages
      const messageHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));
      
      // Add user's new message
      messageHistory.push({
        role: 'user',
        content: input
      });
      
      console.log(`Chatbot - Sending ${messageHistory.length} messages in history`);
      
      // Get authentication token
      const token = await user.getIdToken();
      console.log('Chatbot - Authentication token obtained');
      
      // Send request to the API
      const response = await fetch('/api/chatbot/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          messageHistory,
          systemPrompt,
          savedWords: savedWordsData,
          userLanguageLevel: settings.languageLevel || 'Beginner',
          language
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Chatbot - API error response:', errorText);
        throw new Error('Failed to get response from chatbot');
      }

      const data = await response.json();
      console.log('Chatbot - Response received:', data.text.substring(0, 100) + '...');
      
      // Add assistant response to chat
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: data.text 
      }]);
    } catch (error) {
      console.error('Chatbot - Error sending message:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again later.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleModeChange = (e) => {
    const newMode = e.target.value;
    console.log(`Chatbot - Mode changed from ${chatMode} to ${newMode}`);
    setChatMode(newMode);
  };

  // Add a function to toggle translation for a specific message
  const toggleTranslation = async (index, message) => {
    console.log(`Chatbot - Toggling translation for message ${index}`);
    
    // If already translated, just toggle it back to original
    if (translatedMessages[index]) {
      setTranslatedMessages(prev => ({
        ...prev,
        [index]: false
      }));
      return;
    }
    
    // For the welcome message (index 0), we already have the English translation
    if (index === 0 && message.englishTranslation) {
      setTranslatedMessages(prev => ({
        ...prev,
        [index]: true
      }));
      return;
    }
    
    // For other AI messages, we need to fetch a translation from Gemini
    if (message.role === 'assistant' && !message.translation) {
      try {
        console.log(`Chatbot - Fetching translation for message ${index}`);
        
        // Show loading state for this message
        setMessages(prev => {
          const updatedMessages = [...prev];
          updatedMessages[index] = {
            ...updatedMessages[index],
            isTranslating: true
          };
          return updatedMessages;
        });
        
        // Get the current language
        const currentLanguage = settings.learningLanguage || 'Spanish';
        
        // Get authentication token
        const token = await user.getIdToken();
        
        // Make the API request to translate the text
        const response = await fetch('/api/chatbot/translate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            text: message.content,
            sourceLanguage: currentLanguage
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to translate message');
        }
        
        const data = await response.json();
        console.log('Chatbot - Translation received:', data.translation.substring(0, 50) + '...');
        
        // Update the message with the translation
        setMessages(prev => {
          const updatedMessages = [...prev];
          updatedMessages[index] = {
            ...updatedMessages[index],
            translation: data.translation,
            isTranslating: false
          };
          return updatedMessages;
        });
        
        // Set this message as translated
        setTranslatedMessages(prev => ({
          ...prev,
          [index]: true
        }));
      } catch (error) {
        console.error('Chatbot - Error translating message:', error);
        
        // Remove loading state
        setMessages(prev => {
          const updatedMessages = [...prev];
          updatedMessages[index] = {
            ...updatedMessages[index],
            isTranslating: false
          };
          return updatedMessages;
        });
        
        // Show error to user
        alert('Failed to translate message. Please try again later.');
      }
    } else {
      // Toggle the translation state
      setTranslatedMessages(prev => ({
        ...prev,
        [index]: true
      }));
    }
  };

  // Function to toggle dictionary accordion and ensure only one accordion is open
  const toggleDictionary = () => {
    setDictionaryOpen(!dictionaryOpen);
    if (!dictionaryOpen) {
      setChatModeOpen(false); // Close chat mode when opening dictionary
    }
  };

  // Function to toggle chat mode accordion and ensure only one accordion is open
  const toggleChatMode = () => {
    setChatModeOpen(!chatModeOpen);
    if (!chatModeOpen) {
      setDictionaryOpen(false); // Close dictionary when opening chat mode
    }
  };

  // Function to handle selecting a word for the dictionary panel
  const handleWordSelect = (word, sentence) => {
    setSelectedWord(word);
    setSelectedWordSentence(sentence);
    // Open dictionary accordion and close chat mode when a word is selected
    setDictionaryOpen(true);
    setChatModeOpen(false);
  };

  // Function to get the message content with clickable words for Bojee's messages
  const getMessageContent = (message, index) => {
    // If message is being translated, show loading indicator
    if (message.isTranslating) {
      return "Translating...";
    }
    
    // If showing translation and we have a translation available
    if (translatedMessages[index]) {
      // For the welcome message, we already have the English translation
      if (index === 0 && message.englishTranslation) {
        return message.englishTranslation;
      }
      // For other messages, return the fetched translation
      return message.translation || message.content.replace(/\*/g, '');
    }
    
    // For Bojee's messages, make words clickable
    if (message.role === 'assistant') {
      const content = message.content.replace(/\*/g, '');
      const currentLanguage = settings.learningLanguage || 'Spanish';
      return processChatbotSentence(content, currentLanguage, handleWordSelect);
    }
    
    // For user messages, just return the content
    return message.content;
  };

  // Handler for custom mode checkbox
  const handleCustomModeToggle = (e) => {
    const isChecked = e.target.checked;
    setUseCustomMode(isChecked);
    console.log(`Chatbot - Custom mode ${isChecked ? 'enabled' : 'disabled'}`);
  };

  // Handler for custom prompt text changes
  const handleCustomPromptChange = (e) => {
    setCustomPrompt(e.target.value);
  };
  
  // Toggle custom mode accordion
  const toggleCustomMode = () => {
    setCustomModeOpen(!customModeOpen);
  };

  return (
    <div className="chatbot-container">
      <h1 className="page-title">Chat with Bojee</h1>
      <div className="chatbot-main">
        {/* Left sidebar with accordions */}
        <div className="chatbot-sidebar">
          {/* Dictionary Accordion */}
          <div className="chatbot-accordion-section">
            <div 
              className="chatbot-accordion-header" 
              onClick={toggleDictionary}
            >
              <strong>Dictionary</strong>
              <span className="chatbot-toggle-icon">{dictionaryOpen ? '▲' : '▼'}</span>
            </div>
            <div className={`chatbot-accordion-content ${dictionaryOpen ? 'open' : ''}`}>
              <ChatbotDictionaryPanel
                selectedWord={selectedWord}
                sentence={selectedWordSentence}
                language={settings.learningLanguage}
              />
            </div>
          </div>
          
          {/* Chat Mode Accordion */}
          <div className="chatbot-accordion-section">
            <div 
              className="chatbot-accordion-header"
              onClick={toggleChatMode}
            >
              <strong>Chat Mode</strong>
              <span className="chatbot-toggle-icon">{chatModeOpen ? '▲' : '▼'}</span>
            </div>
            <div className={`chatbot-accordion-content ${chatModeOpen ? 'open' : ''}`}>
              <div className="chatbot-radio-group">
                <label className="chatbot-radio-label">
                  <input
                    type="radio"
                    name="chatMode"
                    value="learning"
                    checked={chatMode === 'learning'}
                    onChange={handleModeChange}
                    disabled={useCustomMode}
                  />
                  Learning Mode
                </label>
                <p className="chatbot-mode-description">
                  Get corrections for language mistakes
                </p>
                <label className="chatbot-radio-label">
                  <input
                    type="radio"
                    name="chatMode"
                    value="casual"
                    checked={chatMode === 'casual'}
                    onChange={handleModeChange}
                    disabled={useCustomMode}
                  />
                  Casual Mode
                </label>
                <p className="chatbot-mode-description">
                  Natural conversation without corrections
                </p>
              </div>
            </div>
          </div>

          {/* Custom Mode Accordion */}
          <div className="chatbot-accordion-section">
            <div 
              className="chatbot-accordion-header"
              onClick={toggleCustomMode}
            >
              <strong>Custom Mode</strong>
              <span className="chatbot-toggle-icon">{customModeOpen ? '▲' : '▼'}</span>
            </div>
            <div className={`chatbot-accordion-content ${customModeOpen ? 'open' : ''}`}>
              <div className="chatbot-radio-group">
                <label className="chatbot-radio-label">
                  <input
                    type="checkbox"
                    checked={useCustomMode}
                    onChange={handleCustomModeToggle}
                  />
                  Select to enable a custom chat bot
                </label>
                <p className="chatbot-mode-description">
                  Use a custom system prompt
                </p>
                <textarea
                  className="chatbot-custom-prompt"
                  value={customPrompt}
                  onChange={handleCustomPromptChange}
                  placeholder="Enter your custom system prompt here..."
                  rows={6}
                  disabled={!useCustomMode}
                />
                <p className="chatbot-mode-description" style={{ marginTop: '10px', fontStyle: 'italic' }}>
                  The system will automatically append language instructions to your prompt.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Chat area remains the same */}
        <div className="chatbot-chat">
          <div className="chatbot-messages" ref={chatContainerRef}>
            {messages.map((message, index) => (
              <div 
                key={index} 
                className={`chatbot-message ${message.role === 'user' ? 'user-message' : 'bot-message'}`}
              >
                <div className="chatbot-message-header">
                  {message.role === 'user' ? username : 'Bojee'}
                </div>
                <div className="chatbot-message-content">
                  {getMessageContent(message, index)}
                  
                  {/* Add translate link for AI messages */}
                  {message.role === 'assistant' && (
                    <div className="translate-link-container">
                      <button 
                        className="translate-link"
                        onClick={() => toggleTranslation(index, message)}
                      >
                        {translatedMessages[index] ? 'Show Original' : 'Translate'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="chatbot-message bot-message">
                <div className="chatbot-message-header">Bojee</div>
                <div className="chatbot-message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          
          <div className="chatbot-input-container">
            <div className="chatbot-input-main">
              <textarea
                className="chatbot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message here..."
                rows={3}
                disabled={isLoading}
              />
              <div className="chatbot-button-container">
                <button 
                  className="chatbot-new-conversation" 
                  onClick={handleNewConversation}
                  title="Start a new conversation"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.89 10.105a8.696 8.696 0 00-.789-1.456l-1.658 1.119a6.606 6.606 0 00-1.958-3.299A6.54 6.54 0 0012 5a6.538 6.538 0 00-3.485 1.469A6.606 6.606 0 006.558 9.77l-1.658-1.119a8.695 8.695 0 00-.789 1.456 8.683 8.683 0 00-.6 1.657l1.82.847a6.57 6.57 0 000 4.781l-1.82.847c.384 1.173.983 2.185 1.388 3.113A8.66 8.66 0 008.04 19l1.658-1.119a6.606 6.606 0 001.958 3.299A6.539 6.539 0 0012 19a6.538 6.538 0 003.485-1.469A6.606 6.606 0 0017.442 14.23l1.658 1.119a8.66 8.66 0 003.11-2.643c.406-.928 1.005-1.94 1.39-3.113l-1.82-.847a6.57 6.57 0 000-4.781l1.82-.847a8.683 8.683 0 00-.6-1.657M12 17a5 5 0 11-.001-10.001A5 5 0 0112 17" />
                  </svg>
                  New Convo
                </button>
                <button 
                  className="chatbot-send-button" 
                  onClick={handleSendMessage}
                  disabled={isLoading || !input.trim()}
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatbotTest; 